import React, { FC, ReactNode } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

interface Column {
  label: string;
  align?: 'left' | 'center' | 'right';
  key?: string;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
}

interface ScrollableTableProps {
  columns: Column[];
  data: any[];
  maxHeight?: number | string;
  onRowClick?: (row: any, index: number) => void;
  renderCell?: (column: Column, row: any, index: number) => ReactNode;
  emptyMessage?: string;
  getRowKey?: (row: any, index: number) => string | number;
}

const ScrollableTable: FC<ScrollableTableProps> = ({
  columns,
  data,
  maxHeight = 350,
  onRowClick,
  renderCell,
  emptyMessage = 'No data available',
  getRowKey,
}) => {
  const defaultRenderCell = (column: Column, row: any, index: number) => {
    const value = column.key ? row[column.key] : null;
    return (
      <Typography variant="body2" sx={{ color: 'text.primary' }}>
        {value ?? '—'}
      </Typography>
    );
  };

  const renderCellContent = renderCell || defaultRenderCell;

  const heightValue = typeof maxHeight === 'number' 
    ? `${maxHeight}px` 
    : maxHeight === '100%' 
    ? '100%' 
    : maxHeight || '350px';

  return (
    <Box
      sx={{
        width: '100%',
        height: maxHeight === '100%' ? '100%' : 'auto',
        minHeight: 0,
        maxHeight: heightValue,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <SimpleBar 
        style={{ 
          maxHeight: heightValue,
          height: maxHeight === '100%' ? '100%' : 'auto',
          width: '100%',
        }}
        autoHide={false}>
        <Box sx={{ pb: 1 }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ 
              boxShadow: 'none', 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 1,
              position: 'relative',
            }}>
            <Table size="small" stickyHeader sx={{ borderCollapse: 'separate' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAFBFC' }}>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={column.align || 'left'}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: '#FAFBFC',
                      whiteSpace: 'nowrap',
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow
                    key={getRowKey ? getRowKey(row, index) : index}
                    onClick={() => onRowClick?.(row, index)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}>
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align={column.align || 'left'}
                        sx={{
                          py: 1.5,
                          fontSize: '0.875rem',
                          borderBottom: index < data.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          width: column.width,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                        }}>
                        {renderCellContent(column, row, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </SimpleBar>
    </Box>
  );
};

export default ScrollableTable;

